import {
  jsonPointerGet,
  parseToolCallParamBindingRef,
} from '@cotera/utilities';
import { PersistedWorkflow } from '@cotera/sdk/core';

const { isNodeScopedFieldPath } = PersistedWorkflow;

/** The parts of an upstream sample this needs; keeps the hook's shape out of here. */
export type AccumulateSourceSample = {
  readonly sourceName: string;
  readonly sample: unknown;
};

/**
 * A sample of one item as the bucket will have stored it, which is what the
 * reducer picker has to offer fields from.
 *
 * A reducer reads a stored item with a bare pointer, and the item is whatever
 * `accumulate` selected — so this has to follow that path exactly, or the
 * picker offers pointers that resolve to `undefined` at flush time. The two
 * forms `accumulate` takes each land somewhere different:
 *
 * - a bare pointer (`/order`, or `''` for the whole value) slices this step's
 *   own input, so the item comes from the direct predecessor's sample;
 * - a `Node/pointer` reference slices that named ancestor's output instead, so
 *   the item comes from *its* sample and the direct predecessor is irrelevant.
 *
 * Returns `undefined` when the named source is not among the samples — an
 * ancestor that has no sample yet — so the caller can offer nothing rather
 * than guess.
 */
export function resolveAccumulatedItemSample(params: {
  readonly accumulate: string;
  readonly directSample: AccumulateSourceSample | undefined;
  readonly upstreamSamples: readonly AccumulateSourceSample[];
}): unknown {
  const path = params.accumulate.trim();
  const parsed = isNodeScopedFieldPath(path)
    ? parseToolCallParamBindingRef(path)
    : undefined;
  const base =
    parsed === undefined
      ? params.directSample
      : params.upstreamSamples.find(
          (sample) => sample.sourceName === parsed.sourceName
        );
  if (base === undefined) {
    return undefined;
  }
  const pointer = parsed?.pointer ?? path;
  // A half-typed path (`ord`) is neither, and must not be read as the whole
  // value — that would offer the upstream's own fields as if they were the
  // item's, which is exactly the pointer that then resolves to nothing.
  if (pointer !== '' && !pointer.startsWith('/')) {
    return undefined;
  }
  return pointer === '' ? base.sample : jsonPointerGet(base.sample, pointer);
}

/**
 * What the reducer picker is offering the fields of, in the words the form
 * uses.
 *
 * "Each item" on its own is the whole confusion: the reducer picker lists a
 * different set of fields from the accumulate picker directly above it, and
 * nothing on screen says the first field decided the second. Naming the source
 * makes the dependency visible where the reader is already looking, and makes
 * it obvious that widening the list means changing what is kept.
 */
export function describeAccumulatedItem(params: {
  readonly accumulate: string;
  readonly sourceLabels: ReadonlyMap<string, string>;
}): string {
  const path = params.accumulate.trim();
  if (path === '') {
    return 'Each item — the whole upstream value';
  }
  if (!isNodeScopedFieldPath(path)) {
    return `Each item — ${path} of this step's input`;
  }
  const { sourceName, pointer } = parseToolCallParamBindingRef(path);
  const label =
    sourceName === undefined ? undefined : params.sourceLabels.get(sourceName);
  const where = label ?? 'an earlier step';
  return pointer === ''
    ? `Each item — ${where}`
    : `Each item — ${pointer} of ${where}`;
}
